// Build "core + módulos assíncronos": o mesmo código do `lib.min.js` que o Pocket carregava pelo
// proxy (mixpanel-browser 2.84.0), sem o gravador de session replay embutido no bundle.
import mixpanel from 'mixpanel-browser/dist/mixpanel-with-async-modules.cjs';

import { TOKENS } from '@/settings/tokens';

type Props = Record<string, unknown>;

// Proxy da Descomplica para a API do Mixpanel: o mesmo `api_host` que o Pocket passava ao `init`.
export const MIXPANEL_API_HOST = 'https://mixpanel.paas.descomplica.com.br';

// Daqui para baixo, porte fiel de `pocket/src/client.js` (1.16.25): `mixpanelProps`,
// `TELEMETRY_DENYLIST`, `mixpanelPeopleProps`, `formatMixpanelProps` e `getMixpanelPeopleProps`.
// Mudar um destes mapas muda o nome da propriedade que chega ao Mixpanel.

// Nome comum → propriedade reservada do Mixpanel, comparado em minúsculas. `Map`, e não objeto,
// para que uma chave como `constructor` não case com o protótipo.
const RESERVED_PROPS = new Map([
  ['created', '$created'],
  ['email', '$email'],
  ['name', '$name'],
  ['firstname', '$firstName'],
  ['lastname', '$lastName'],
  ['phone', '$phone'],
]);

// Chaves que nunca saem para o Mixpanel. O cookie `d` é o token de sessão do aluno e do operador.
const TELEMETRY_DENYLIST = new Set([
  'd',
  'd-profile',
  'token',
  'accesstoken',
  'access_token',
  'refreshtoken',
  'refresh_token',
  'authorization',
  'password',
  'senha',
]);

// No perfil (`people.set`), só estas chaves (comparação exata) viram propriedade reservada.
const PEOPLE_RESERVED_KEYS = new Set([
  'created',
  'email',
  'name',
  'firstName',
  'lastName',
  'phone',
  '$created',
  '$email',
  '$name',
  '$firstname',
  '$lastname',
  '$phone',
  'is_subscriber',
  'Cpf',
  'PaymentId',
  'PaymentType',
  'ProductName',
  'ProductValue',
  'promocode',
]);

const isSensitiveProp = (key: string): boolean => TELEMETRY_DENYLIST.has(key.toLocaleLowerCase());

/** Propriedades de evento e super properties, como o Pocket mandava. */
export const formatMixpanelProps = (props: Props): Props => {
  const formatted: Props = {};
  for (const key of Object.keys(props)) {
    if (!isSensitiveProp(key)) {
      formatted[RESERVED_PROPS.get(key.toLocaleLowerCase()) || key] = props[key];
    }
  }
  return formatted;
};

/** Propriedades do perfil (`people.set`), ou `undefined` quando não sobra nada para gravar. */
export const getMixpanelPeopleProps = (props: Props): Props | undefined => {
  const peopleProps: Props = {};
  for (const key of Object.keys(props)) {
    if (!isSensitiveProp(key)) {
      const reservedProp = RESERVED_PROPS.get(key.toLocaleLowerCase());
      peopleProps[PEOPLE_RESERVED_KEYS.has(key) && reservedProp ? reservedProp : key] = props[key];
    }
  }
  return Object.keys(peopleProps).length > 0 ? peopleProps : undefined;
};

let isInitialized = false;

/**
 * A instância do Mixpanel, inicializada uma única vez e só no navegador. No build (SSR/export) e
 * sem token devolve `undefined`, como o Pocket, que não carregava o Mixpanel sem token.
 */
const getMixpanel = (): typeof mixpanel | undefined => {
  if (typeof window === 'undefined' || !TOKENS.mixpanel) return undefined;

  if (!isInitialized) {
    isInitialized = true;
    // Session replay e heatmap ficam desligados (padrão do SDK), como estavam: o Pocket só os
    // ligava com `enableHeatmaps` no `POCKET_ENV`, que este repo nunca passou.
    mixpanel.init(TOKENS.mixpanel, { api_host: MIXPANEL_API_HOST });
    // O GTM e o Pocket/Zé Spot leem `window.mixpanel` (o `$device_id`, por exemplo). O Pocket não
    // manda nada por ele: não recebe token no `POCKET_ENV` e é chamado com `useMixpanel: false`.
    window.mixpanel = mixpanel;
  }

  return mixpanel;
};

/** Único ponto do repo que fala com o `mixpanel-browser`. Telemetria nunca quebra a tela. */
export const MixpanelClient = {
  identify(userId: string, traits: Props): void {
    try {
      const client = getMixpanel();
      if (!client) return;

      client.identify(userId);
      const peopleProps = getMixpanelPeopleProps(traits);
      if (peopleProps) client.people.set(peopleProps);
      // O Pocket registrava os traits do `identify` como super properties de todo evento.
      client.register(formatMixpanelProps(traits));
    } catch {
      // sem Mixpanel o painel segue funcionando
    }
  },

  track(eventName: string, props: Props): void {
    try {
      getMixpanel()?.track(eventName, formatMixpanelProps(props));
    } catch {
      // sem Mixpanel o painel segue funcionando
    }
  },
};
